/**
 * CreditNoteDialog — Feature 3: Raise a Credit Note
 *
 * Handles two distinct flows:
 *   • T&M projects  — full credit note only; user chooses "Return to WIP" or "Write Off"
 *   • Fixed Fee     — full or partial credit note; user enters amount if partial
 *
 * All state mutations are LOCAL (mock) only. Each mutation point is marked with
 * a TODO comment for engineering to replace with the real API call.
 *
 * Exported types:
 *   CreditNoteResult  — the resolved data returned after the dialog is confirmed
 *   CreditNoteDialogProps — props for the dialog component
 */

import React, { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { AlertCircle, ArrowLeft, CheckCircle, CreditCard, Info } from "lucide-react";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type CreditNoteType = "full" | "partial";
export type TamDisposition = "return-to-wip" | "write-off";

/**
 * The resolved result handed back to the parent component after the user
 * confirms the credit note. Engineering replaces the onConfirm handler with
 * a real API call (POST /api/invoices/{id}/credit-note).
 */
export interface CreditNoteResult {
  /** The invoice ID being credited */
  invoiceId: number;
  /** The original invoice number (display only) */
  invoiceNumber: string | null;
  /** "full" or "partial" */
  creditType: CreditNoteType;
  /** Amount being credited (= invoice amount for full; user-entered for partial) */
  creditAmount: number;
  /**
   * T&M only — what to do with the WIP items on the original invoice.
   * undefined for Fixed Fee projects.
   */
  tamDisposition?: TamDisposition;
  /**
   * T&M only, write-off path — the write-off reason selected by the user.
   * undefined for Fixed Fee or return-to-wip path.
   */
  writeOffReason?: string;
}

export interface CreditNoteDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Called when the dialog is closed without confirming */
  onClose: () => void;
  /**
   * Called when the user confirms the credit note.
   * The parent component uses this to apply local mock state changes and then
   * show a success toast.
   * TODO (Engineering): Replace the onConfirm handler in the parent with a
   *   real API mutation:  POST /api/invoices/{invoiceId}/credit-note
   *   Body: { creditType, creditAmount, tamDisposition, writeOffReason }
   */
  onConfirm: (result: CreditNoteResult) => void;
  /** Invoice being credited */
  invoice: {
    id: number;
    invoiceNumber: string | null;
    amount: number;
    status: string;
  };
  /**
   * Project type determines the credit note flow.
   * "time-and-materials" → T&M path (return to WIP / write off, full credit only)
   * "fixed-price"        → Fixed Fee path (full or partial)
   */
  projectType: "time-and-materials" | "fixed-price";
  /** Currency formatter */
  formatCurrency: (value: number) => string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Write-off reasons (mock data — engineering replaces with API lookup)
// TODO (Engineering): Fetch write-off reasons from
//   GET /api/settings/write-off-reasons
//   Replace the WRITE_OFF_REASONS constant with the API response.
// ─────────────────────────────────────────────────────────────────────────────
const WRITE_OFF_REASONS = [
  "Client Discount",
  "Goodwill Gesture",
  "Error / Correction",
  "Budget Overrun",
  "Negotiated Settlement",
  "Internal — Not Billable",
  "Duplicate Invoice",
  "Other",
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Helper: generate a mock credit note number
// TODO (Engineering): Credit note number is assigned server-side on POST.
//   Remove this helper once the real API returns the CN number.
// ─────────────────────────────────────────────────────────────────────────────
function generateCreditNoteNumber(invoiceNumber: string | null): string {
  const base = invoiceNumber ? invoiceNumber.replace("INV", "CN") : "CN";
  return `${base}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Step indicator
// ─────────────────────────────────────────────────────────────────────────────
function StepIndicator({ step, total }: { step: number; total: number }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          <div
            className={cn(
              "h-6 w-6 rounded-full flex items-center justify-center text-xs font-semibold transition-colors",
              i + 1 === step
                ? "bg-primary text-primary-foreground"
                : i + 1 < step
                ? "bg-success text-success-foreground"
                : "bg-muted text-muted-foreground"
            )}
          >
            {i + 1 < step ? <CheckCircle className="h-3.5 w-3.5" /> : i + 1}
          </div>
          {i < total - 1 && (
            <div className={cn("h-px flex-1 transition-colors", i + 1 < step ? "bg-success" : "bg-border")} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
export function CreditNoteDialog({
  open,
  onClose,
  onConfirm,
  invoice,
  projectType,
  formatCurrency,
}: CreditNoteDialogProps) {
  const isTam = projectType === "time-and-materials";

  // ── Dialog state ────────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2>(1);
  const [creditType, setCreditType] = useState<CreditNoteType>("full");
  const [partialAmount, setPartialAmount] = useState<string>("");
  // Raw numeric string while the user is actively typing (no formatting applied yet)
  const [partialAmountRaw, setPartialAmountRaw] = useState<string>("");
  const [tamDisposition, setTamDisposition] = useState<TamDisposition>("return-to-wip");
  const [writeOffReason, setWriteOffReason] = useState<string>("");
  const [partialAmountError, setPartialAmountError] = useState<string>("");

  // ── Derived values ──────────────────────────────────────────────────────────
  const resolvedCreditAmount =
    creditType === "full"
      ? invoice.amount
      : parseFloat(partialAmountRaw || partialAmount.replace(/,/g, "")) || 0;

  const creditNoteNumber = generateCreditNoteNumber(invoice.invoiceNumber);
  const isPartialValid =
    creditType === "full" ||
    (resolvedCreditAmount > 0 && resolvedCreditAmount <= invoice.amount);

  const isWriteOffReasonRequired = isTam && tamDisposition === "write-off";
  const canProceedStep1 = isTam
    ? isWriteOffReasonRequired ? writeOffReason.trim() !== "" : true
    : isPartialValid;

  // ── Handlers ─────────────────────────────────────────────────────────────────
  function handleReset() {
    setStep(1);
    setCreditType("full");
    setPartialAmount("");
    setPartialAmountRaw("");
    setTamDisposition("return-to-wip");
    setWriteOffReason("");
    setPartialAmountError("");
  }

  function handleClose() {
    handleReset();
    onClose();
  }

  function validatePartialAmount(num: number) {
    if (isNaN(num) || num <= 0) {
      setPartialAmountError("Please enter a valid amount greater than zero.");
    } else if (num > invoice.amount) {
      setPartialAmountError(`Amount cannot exceed the invoice total of ${formatCurrency(invoice.amount)}.`);
    } else {
      setPartialAmountError("");
    }
  }

  // While typing: accept raw numeric input (strip anything non-numeric except decimal point)
  function handlePartialAmountChange(value: string) {
    const stripped = value.replace(/[^0-9.]/g, "");
    // Prevent more than one decimal point
    const parts = stripped.split(".");
    const sanitised = parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : stripped;
    setPartialAmountRaw(sanitised);
    setPartialAmount(sanitised); // keep in sync so validation & remaining calc work
    validatePartialAmount(parseFloat(sanitised));
  }

  // On blur: format to 2 decimal places with thousands separator
  function handlePartialAmountBlur() {
    const num = parseFloat(partialAmountRaw);
    if (!isNaN(num) && num > 0) {
      const formatted = num.toLocaleString("en-GB", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      setPartialAmount(formatted);
      setPartialAmountRaw(String(num));
    }
    validatePartialAmount(num);
  }

  // On focus: strip formatting so the user can edit the raw number easily
  function handlePartialAmountFocus() {
    const num = parseFloat(partialAmountRaw || partialAmount.replace(/,/g, ""));
    if (!isNaN(num) && num > 0) {
      setPartialAmount(String(num));
      setPartialAmountRaw(String(num));
    }
  }

  function handleReview() {
    if (!canProceedStep1) return;
    setStep(2);
  }

  function handleConfirm() {
    const result: CreditNoteResult = {
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      creditType,
      creditAmount: resolvedCreditAmount,
      tamDisposition: isTam ? tamDisposition : undefined,
      writeOffReason: isTam && tamDisposition === "write-off" ? writeOffReason : undefined,
    };
    handleReset();
    onConfirm(result);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Render helpers
  // ─────────────────────────────────────────────────────────────────────────────

  /** Step 1 — T&M: choose disposition */
  function renderTamStep1() {
    return (
      <div className="space-y-5">
        {/* Info banner */}
        <div className="flex gap-3 rounded-md border border-info-200 bg-info-50 p-3 text-sm text-info-800">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-info-500" />
          <div>
            <p className="font-medium">Full credit note</p>
            <p className="mt-0.5 text-info-700">
              This will raise a credit note for the full invoice amount of{" "}
              <strong>{formatCurrency(invoice.amount)}</strong> against{" "}
              <strong>{invoice.invoiceNumber ?? `Invoice #${invoice.id}`}</strong>.
              Choose what happens to the time entries that were on this invoice.
            </p>
          </div>
        </div>

        {/* Disposition choice */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold">What should happen to the WIP items?</Label>
          <RadioGroup
            value={tamDisposition}
            onValueChange={(v) => {
              setTamDisposition(v as TamDisposition);
              setWriteOffReason("");
            }}
            className="gap-3"
          >
            {/* Return to WIP */}
            <div
              className={cn(
                "rounded-lg border p-4 cursor-pointer transition-colors",
                tamDisposition === "return-to-wip"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
              onClick={() => {
                setTamDisposition("return-to-wip");
                setWriteOffReason("");
              }}
            >
              <RadioGroupItem value="return-to-wip">
                Return all time entries to WIP
              </RadioGroupItem>
              <p className="mt-1.5 ml-7 text-xs text-muted-foreground">
                All time &amp; expenses from this invoice will be returned to Unbilled Work and can
                be re-invoiced. Use this when the invoice was raised in error or needs to be re-billed
                at a different rate.
              </p>
            </div>

            {/* Write off */}
            <div
              className={cn(
                "rounded-lg border p-4 cursor-pointer transition-colors",
                tamDisposition === "write-off"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
              onClick={() => setTamDisposition("write-off")}
            >
              <RadioGroupItem value="write-off">
                Write off
              </RadioGroupItem>
              <p className="mt-1.5 ml-7 text-xs text-muted-foreground">
                The time entries will be permanently written off and will not appear in WIP again.
                Use this for client discounts, goodwill gestures, or corrections.
              </p>

              {/* Write-off reason — shown only when write-off is selected */}
              {tamDisposition === "write-off" && (
                <div className="mt-3 ml-7 space-y-1.5">
                  <Label htmlFor="write-off-reason" className="text-xs font-medium">
                    Reason <span className="text-destructive">*</span>
                  </Label>
                  {/*
                    TODO (Engineering): Replace WRITE_OFF_REASONS with API response from
                    GET /api/settings/write-off-reasons — may also be conditional on
                    the "require write-off reason" company setting.
                  */}
                  <Select value={writeOffReason} onValueChange={setWriteOffReason}>
                    <SelectTrigger id="write-off-reason" className="h-8 text-sm">
                      <SelectValue placeholder="Select a reason…" />
                    </SelectTrigger>
                    <SelectContent>
                      {WRITE_OFF_REASONS.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isWriteOffReasonRequired && !writeOffReason && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> A reason is required to write off.
                    </p>
                  )}
                </div>
              )}
            </div>
          </RadioGroup>
        </div>
      </div>
    );
  }

  /** Step 1 — Fixed Fee: choose full or partial */
  function renderFixedFeeStep1() {
    return (
      <div className="space-y-5">
        {/* Info banner */}
        <div className="flex gap-3 rounded-md border border-info-200 bg-info-50 p-3 text-sm text-info-800">
          <Info className="h-4 w-4 mt-0.5 shrink-0 text-info-500" />
          <p>
            Raising a credit note against{" "}
            <strong>{invoice.invoiceNumber ?? `Invoice #${invoice.id}`}</strong>{" "}
            ({formatCurrency(invoice.amount)}). Choose whether to credit the full amount or a
            partial amount.
          </p>
        </div>

        {/* Credit type */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold">Credit note type</Label>
          <RadioGroup
            value={creditType}
            onValueChange={(v) => {
              setCreditType(v as CreditNoteType);
              setPartialAmount("");
              setPartialAmountRaw("");
              setPartialAmountError("");
            }}
            className="gap-3"
          >
            {/* Full */}
            <div
              className={cn(
                "rounded-lg border p-4 cursor-pointer transition-colors",
                creditType === "full"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
              onClick={() => {
                setCreditType("full");
                setPartialAmount("");
                setPartialAmountError("");
              }}
            >
              <RadioGroupItem value="full">
                Full credit note — {formatCurrency(invoice.amount)}
              </RadioGroupItem>
              <p className="mt-1.5 ml-7 text-xs text-muted-foreground">
                Credits the entire invoice. The invoice status will change to{" "}
                <strong>Credited</strong> and the milestone will return to Unbilled.
              </p>
            </div>

            {/* Partial */}
            <div
              className={cn(
                "rounded-lg border p-4 cursor-pointer transition-colors",
                creditType === "partial"
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              )}
              onClick={() => setCreditType("partial")}
            >
              <RadioGroupItem value="partial">
                Partial credit note
              </RadioGroupItem>
              <p className="mt-1.5 ml-7 text-xs text-muted-foreground">
                Credits a portion of the invoice. The invoice status will change to{" "}
                <strong>Partially Credited</strong>.
              </p>

              {/* Partial amount input */}
              {creditType === "partial" && (
                <div className="mt-3 ml-7 space-y-1.5">
                  <Label htmlFor="partial-amount" className="text-xs font-medium">
                    Credit amount <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex h-8 w-full rounded-md border border-input bg-background text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                    <span className="flex items-center pl-3 pr-1 text-sm text-muted-foreground select-none">
                      £
                    </span>
                    <input
                      id="partial-amount"
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={partialAmount}
                      onChange={(e) => handlePartialAmountChange(e.target.value)}
                      onBlur={handlePartialAmountBlur}
                      onFocus={handlePartialAmountFocus}
                      className="flex-1 bg-transparent py-1 pr-3 text-sm outline-none placeholder:text-muted-foreground"
                    />
                  </div>
                  {partialAmountError && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" /> {partialAmountError}
                    </p>
                  )}
                  {partialAmount && !partialAmountError && (
                    <p className="text-xs text-muted-foreground">
                      Remaining after credit:{" "}
                      <strong>
                        {formatCurrency(invoice.amount - (parseFloat(partialAmountRaw || partialAmount.replace(/,/g, "")) || 0))}
                      </strong>
                    </p>
                  )}
                </div>
              )}
            </div>
          </RadioGroup>
        </div>
      </div>
    );
  }

  /** Step 2 — Confirmation summary (both project types) */
  function renderStep2() {
    const isFullCredit = creditType === "full";
    const newInvoiceStatus = isFullCredit ? "Credited" : "Partially Credited";

    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Please review the details below before confirming. This action cannot be undone.
        </p>

        {/* Summary card */}
        <div className="rounded-lg border bg-muted/30 divide-y text-sm">
          <div className="flex justify-between px-4 py-3">
            <span className="text-muted-foreground">Original invoice</span>
            <span className="font-medium">{invoice.invoiceNumber ?? `Invoice #${invoice.id}`}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-muted-foreground">Credit note number</span>
            <span className="font-medium">{creditNoteNumber}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-muted-foreground">Credit amount</span>
            <span className="font-semibold text-base">{formatCurrency(resolvedCreditAmount)}</span>
          </div>
          <div className="flex justify-between px-4 py-3">
            <span className="text-muted-foreground">Invoice status after credit</span>
            <Badge variant="secondary">{newInvoiceStatus}</Badge>
          </div>

          {/* T&M-specific rows */}
          {isTam && (
            <>
              <div className="flex justify-between px-4 py-3">
                <span className="text-muted-foreground">WIP disposition</span>
                <span className="font-medium">
                  {tamDisposition === "return-to-wip" ? "Return all entries to WIP" : "Write off"}
                </span>
              </div>
              {tamDisposition === "write-off" && writeOffReason && (
                <div className="flex justify-between px-4 py-3">
                  <span className="text-muted-foreground">Write-off reason</span>
                  <span className="font-medium">{writeOffReason}</span>
                </div>
              )}
            </>
          )}

          {/* Fixed Fee partial */}
          {!isTam && creditType === "partial" && (
            <div className="flex justify-between px-4 py-3">
              <span className="text-muted-foreground">Remaining invoice value</span>
              <span className="font-medium">
                {formatCurrency(invoice.amount - resolvedCreditAmount)}
              </span>
            </div>
          )}
        </div>

        {/* What will happen */}
        <div className="rounded-md border border-warning-200 bg-warning-50 p-3 text-xs text-warning-800 space-y-1">
          <p className="font-semibold flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" /> What will happen:
          </p>
          <ul className="list-disc ml-4 space-y-0.5">
            <li>
              A credit note row (<strong>{creditNoteNumber}</strong>) will appear in the invoice
              table, linked to the original invoice.
            </li>
            <li>
              The original invoice status will update to{" "}
              <strong>{newInvoiceStatus}</strong>.
            </li>
            {isTam && tamDisposition === "return-to-wip" && (
              <li>All time &amp; expense entries will be returned to <strong>Unbilled Work</strong>.</li>
            )}
            {isTam && tamDisposition === "write-off" && (
              <li>
                All time &amp; expense entries will be <strong>written off</strong> (
                {writeOffReason}).
              </li>
            )}
            {!isTam && isFullCredit && (
              <li>The associated milestone will return to <strong>Unbilled</strong> status.</li>
            )}
            <li>
              The <strong>Total Invoiced</strong> metric will be reduced by{" "}
              {formatCurrency(resolvedCreditAmount)}.
            </li>
          </ul>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────────
  const totalSteps = 2;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-lg" allowOutsideClick>
        <DialogHeader>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <CreditCard className="h-4 w-4 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base">
                Raise Credit Note
              </DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                {invoice.invoiceNumber ?? `Invoice #${invoice.id}`} · {formatCurrency(invoice.amount)}
              </p>
            </div>
          </div>
          <StepIndicator step={step} total={totalSteps} />
        </DialogHeader>

        <Separator />

        <div className="py-1">
          {step === 1
            ? isTam
              ? renderTamStep1()
              : renderFixedFeeStep1()
            : renderStep2()}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          {step === 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep(1)}
              className="mr-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
          )}
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {step === 1 ? (
            <Button onClick={handleReview} disabled={!canProceedStep1}>
              Review →
            </Button>
          ) : (
            <Button onClick={handleConfirm}>
              Confirm Credit Note
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
